<template>
  <div class="max-w-full bg-primary overflow-x-hidden">
    <!-- 视频内容 - 全屏显示 -->
    <div class="video-container">
      <!-- PC 视频 -->
        <video
    v-if="isPcView"
            ref="pcVideoRef"
    class="video-element"
    autoplay muted loop preload="auto" playsinline
    @loadedmetadata="onVideoLoaded"
    @error="onVideoError"
        >
          <source :src="getCdnVideo('/images/website/pc_video.mp4')" type="video/mp4">
        </video>

<!-- 移动端：统一使用 GIF 图片 -->
<picture v-if="!isPcView" class="mobile-image-element">
  <img ref="mobileImageRef" :src="mobileImageSrc" alt="" class="mobile-image-element" @load="onMobileImageLoad"/>
</picture>
      <!-- 透明遮罩层，阻止“点按”触发视频控制，但不影响手势滑动滚动页面 -->
      <div class="video-mask" @click.stop.prevent></div>
      </div>
    <!-- 页面内容 - 在视频下方 -->
    <div class="content-section">
      <!-- 星空背景，仅作用于表单蓝色区域 -->
      <div id="stars" class="stars-background"></div>
      <!-- 填写区域 -->
      <div class="flex flex-col items-center fill-area bg-transparent">
        <div
            class="text-white contact-us">
          联系我们
        </div>
        <div class="input-row">
          <div class="input-frame"></div>
          <span class="input-label-inside text-white">姓名：</span>
        <input
            v-model="name"
              class="input-overlay text-white"
              type="text"
              autocomplete="off"
        />
        </div>
        <div class="input-row">
          <div class="input-frame"></div>
          <span class="input-label-inside text-white">电话：</span>
        <input
            v-model="phone"
              class="input-overlay text-white"
              type="text"
              autocomplete="off"
        />
        </div>
        <div class="input-row">
          <div class="input-frame"></div>
          <span class="input-label-inside text-white">单位：</span>
        <input
            v-model="unit"
              class="input-overlay text-white"
              type="text"
              autocomplete="off"
        />
        </div>
        <button
            class="text-white max-w-full button-style"
            @click="submitForm"
        >提交</button>
        <div
            class=" text-white footer-copyright"
        >
          Copyright © 2017--2026 福州亚历山大健康管理有限公司 All Rights Reserved. 闽ICP备14001762号-1
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { onMounted, onUnmounted, ref, watchEffect, nextTick } from 'vue';
import { ElMessage } from 'element-plus';

const name = ref('');
const phone = ref('');
const unit = ref('');

// 表单提交函数
const submitForm = async () => {
  const trimmedName = name.value.trim();
  const trimmedPhone = phone.value.trim();
  const trimmedUnit = unit.value.trim();

  // 三个字段都不能为空
  if (!trimmedName) {
    ElMessage.warning('请填写姓名');
    return;
  }
  if (!trimmedPhone) {
    ElMessage.warning('请填写联系电话');
    return;
  }
  if (!trimmedUnit) {
    ElMessage.warning('请填写单位名称');
    return;
  }

  const data = {
    full_name: trimmedName,
    telephone: trimmedPhone,
    company: trimmedUnit
  };

  try {
    // 开发环境使用代理，生产环境使用完整 URL
    const apiUrl = process.env.NODE_ENV === 'development'
        ? '/api/webApply/webJoinApply'
        : 'https://backstage.mralex.cn/webApply/webJoinApply';

    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    });

    const result = await response.json();

    if (result.code === 0) {
      ElMessage.success('提交成功！');
      name.value = '';
      phone.value = '';
      unit.value = '';
    } else {
      ElMessage.error('提交失败：' + (result.msg || '未知错误'));
    }
  } catch (error) {
    console.error('网络请求出错:', error);
    ElMessage.error('网络连接失败，请稍后再试。');
  }
}

// 用于判断当前是 PC 端视图还是移动端视图
// 使用函数安全地获取窗口宽度，避免 SSR 或初始化时的问题
const getIsPcView = () => {
  if (typeof window !== 'undefined') {
    return window.innerWidth > 1125;
  }
  return false;
};

const isPcView = ref(getIsPcView());

// 视频元素的引用
const pcVideoRef = ref(null);
const mobileVideoRef = ref(null);
// 移动端图片元素的引用
const mobileImageRef = ref(null);

// 简单日志助手，便于排查移动端渲染/播放阻塞
const logVideo = (...args) => {
  // 避免在生产环境产生过多日志，可按需注释
  console.log('[Video]', ...args);
};

// 事件调试：打印关键视频事件与 readyState、networkState
const attachVideoDebugEvents = (videoEl, label) => {
  if (!videoEl) return;
  const events = ['loadedmetadata', 'loadeddata', 'canplay', 'canplaythrough', 'stalled', 'waiting', 'playing', 'play', 'pause', 'error', 'progress'];
  events.forEach(evt => {
    videoEl.addEventListener(evt, () => {
      logVideo(`${label} event: ${evt}`, 'readyState:', videoEl.readyState, 'networkState:', videoEl.networkState, 'currentTime:', videoEl.currentTime);
    });
  });
};

// 更新视图类型 (PC 或 移动端)
const updateViewType = () => {
  isPcView.value = getIsPcView();
  logVideo('updateViewType', 'width:', typeof window !== 'undefined' ? window.innerWidth : 'N/A', 'isPcView:', isPcView.value);
  // 如果是移动端，重新调整容器高度并打印图片尺寸信息
  if (!isPcView.value) {
    adjustContainerHeight();
  }
};

// 根据图片原始宽高比动态设置容器和图片高度
const adjustContainerHeight = () => {
  if (typeof window === 'undefined' || !mobileImageRef.value) return;

  const imgElement = mobileImageRef.value;
  const screenWidth = window.innerWidth;
  const originalWidth = imgElement.naturalWidth;
  const originalHeight = imgElement.naturalHeight;

  if (originalWidth > 0 && originalHeight > 0) {
    // 根据原始宽高比计算高度
    const aspectRatio = originalHeight / originalWidth;
    const calculatedHeight = screenWidth * aspectRatio;

    // 直接设置图片的宽高，避免缩放导致的模糊
    imgElement.style.width = `${screenWidth}px`;
    imgElement.style.height = `${calculatedHeight}px`;

    // 设置视频容器高度
    const videoContainer = imgElement.closest('.video-container');
    if (videoContainer) {
      videoContainer.style.height = `${calculatedHeight}px`;
    }

    // 设置 picture 容器高度
    const pictureElement = imgElement.parentElement;
    if (pictureElement && pictureElement.tagName === 'PICTURE') {
      pictureElement.style.width = `${screenWidth}px`;
      pictureElement.style.height = `${calculatedHeight}px`;
    }
  }
};

// 移动端图片加载完成事件
const onMobileImageLoad = () => {
  // 先调整容器高度
  adjustContainerHeight();
};


// 播放对应设备的视频
const playVideo = async () => {
  if (isPcView.value) {
    if (!pcVideoRef.value) return;
    logVideo('playVideo start (PC)', 'pc readyState:', pcVideoRef.value.readyState);
    // PC 端视图：播放 PC 视频，暂停移动端视频
    try {
      await pcVideoRef.value.play();
      logVideo('pc play ok');
    } catch (e) {
      logVideo("PC video play error:", e);
    }
  } else {
    if (!isMobileVideoMp4.value || !mobileVideoRef.value) {
      logVideo('playVideo skip mobile video (not mp4 or no ref)');
      return;
    }
    logVideo('playVideo start (Mobile)', 'mobile readyState:', mobileVideoRef.value.readyState);
    // 移动端视图：播放移动端视频，暂停 PC 视频
    try {
      await mobileVideoRef.value.play();
      logVideo('mobile play ok');
    } catch (e) {
      logVideo("Mobile video play error:", e, 'readyState:', mobileVideoRef.value.readyState);
    }
  }
};

// 监听 isPcView 的变化，控制视频播放/暂停
watchEffect(() => {
  playVideo();
});

const getCdnVideo = (path) => {
  return `https://alex-master-1254393188.file.myqcloud.com${path}?v=${Date.now()}&cache=wx`;
};

// 视频加载完成事件
const onVideoLoaded = (event) => {
  const video = event.target;

  // 视频加载成功，重置重试计数
  resetVideoRetryCount();
  // 确保视频在加载后自动播放（微信浏览器特殊处理）
  if (!isPcView.value && video.paused) {
    if (isWeChatBrowser.value) {
      // 微信浏览器中，等待 WeixinJSBridge 就绪
      const tryPlay = () => {
        if (typeof window.WeixinJSBridge !== 'undefined' && window.WeixinJSBridge.ready) {
          video.play().catch(e => {
            console.log("Video play failed in WeChat:", e);
            // 延迟重试
            setTimeout(() => {
              video.play().catch(() => {
                console.log("Retry play failed");
              });
            }, 500);
          });
        } else {
          // 如果 WeixinJSBridge 未就绪，延迟重试
          setTimeout(() => {
            tryPlay();
          }, 300);
        }
      };
      tryPlay();
    } else {
      // 非微信浏览器，直接尝试播放
      video.play().catch(e => {
        console.log("Auto-play failed, retrying...", e);
        setTimeout(() => {
          video.play().catch(err => {
            console.log("Retry play failed:", err);
          });
        }, 500);
      });
    }
  }
};

// 视频错误处理
const onVideoError = (event) => {
  console.error("Video error:", event);
  const video = event.target;

  if (video.error) {
    const errorCode = video.error.code;
    const errorMessages = {
      1: "视频加载被中止",
      2: "网络错误导致视频下载失败",
      3: "视频解码失败",
      4: "视频格式不支持或服务器/网络故障"
    };

    console.error(`视频错误 ${errorCode}: ${errorMessages[errorCode] || "未知错误"}`);

    // 对于严重错误，启用回退策略
    if (errorCode === 3 || errorCode === 4) {
      console.warn("遇到严重视频错误，启用回退策略");
      handleVideoPlaybackFailure();
    } else {
      // 对于网络相关错误，尝试重新加载
      setTimeout(() => {
        console.log("尝试重新加载视频...");
        video.load();
      }, 3000);
    }
  } else {
    // 未知错误，也启用回退策略
    console.error("未知视频错误");
    handleVideoPlaybackFailure();
  }
};

// 检测是否为微信浏览器
const checkWeChatBrowser = () => {
  if (typeof window !== 'undefined' && window.navigator) {
    const ua = window.navigator.userAgent.toLowerCase();
    return /micromessenger/.test(ua);
    }
  return false;
};

const isWeChatBrowser = ref(checkWeChatBrowser());

// ------------------ 移动端背景图选择（统一使用 GIF） ------------------
const mobileImageSrc = ref('');
const mobileImageMeta = ref({ isAndroid: false, isIOS: false, reason: '未检测' });
const isMobileVideoMp4 = ref(false); // 统一使用 GIF，不走 mp4 视频分支
const isGifLoaded = ref(false); // GIF 是否已加载完成

const chooseMobileImage = () => {
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent || '' : '';
  const isAndroid = /android/i.test(ua);
  const isIOS = /iphone|ipad|ipod/i.test(ua);

  console.log('[MobileBackground] 开始检测 UA', ua);

  // 初始化时先使用静态 JPG 图片
  const initialFile = 'mobile_image.jpg';
  const reason = '初始化使用静态 JPG，等待 GIF 加载';

  mobileImageMeta.value = { isAndroid, isIOS, reason, ua };
  mobileImageSrc.value = getCdnVideo(`/images/website/${initialFile}`);
  console.log('[MobileBackground] 初始化使用 JPG', { isAndroid, isIOS, reason, file: initialFile, ua });
};

// 预加载 GIF 并在加载完成后切换
const preloadGif = () => {
  const gifUrl = getCdnVideo('/images/website/mobile_video.gif');
  console.log('[MobileBackground] 开始预加载 GIF', gifUrl);
  
  const img = new Image();
  
  img.onload = () => {
    console.log('[MobileBackground] GIF 加载完成，切换到 GIF');
    isGifLoaded.value = true;
    mobileImageSrc.value = gifUrl;
    mobileImageMeta.value.reason = 'GIF 加载完成，已切换到 GIF';
    // GIF 切换后，延迟调整容器高度
    setTimeout(() => {
      adjustContainerHeight();
    }, 200);
  };
  
  img.onerror = () => {
    console.error('[MobileBackground] GIF 加载失败，继续使用 JPG');
    mobileImageMeta.value.reason = 'GIF 加载失败，继续使用 JPG';
  };
  
  // 开始加载
  img.src = gifUrl;
};


// 视频播放状态管理
const videoPlaybackFailed = ref(false);
const videoRetryCount = ref(0);
const maxVideoRetries = 3;

// 视频播放失败处理
const handleVideoPlaybackFailure = () => {
  videoRetryCount.value++;
  logVideo('handleVideoPlaybackFailure', 'retry:', videoRetryCount.value);

  if (videoRetryCount.value >= maxVideoRetries) {
    console.warn("视频播放多次失败，启用回退策略");
    videoPlaybackFailed.value = true;

    // 显示静态背景作为回退
    const videoContainer = document.querySelector('.video-container');
    if (videoContainer) {
      videoContainer.style.backgroundColor = '#1a237e';
      videoContainer.style.backgroundImage = 'linear-gradient(135deg, #1a237e 0%, #3f51b5 100%)';
    }

    // 隐藏视频元素
    const videoElement = isPcView.value ? pcVideoRef.value : mobileVideoRef.value;
    if (videoElement) {
      videoElement.style.display = 'none';
    }

    console.log("已切换到静态背景模式");
  } else {
    // 继续重试
    setTimeout(() => {
      console.log(`视频播放第 ${videoRetryCount.value} 次重试`);
      playVideo();
    }, 2000 * videoRetryCount.value); // 递增延迟
  }
};

// 重置视频重试计数
const resetVideoRetryCount = () => {
  videoRetryCount.value = 0;
  videoPlaybackFailed.value = false;
};

onMounted(() => {
  // 先确定移动端背景图的选择（初始化使用 JPG）
  chooseMobileImage();
  // 明确打印一次当前的背景资源与 UA
  if (mobileImageMeta.value) {
    console.log('[MobileBackground] 当前加载资源', {
      file: mobileImageSrc.value,
      isAndroid: mobileImageMeta.value.isAndroid,
      isIOS: mobileImageMeta.value.isIOS,
      reason: mobileImageMeta.value.reason,
      ua: mobileImageMeta.value.ua
    });
  }
  // 再做一次延迟打印，确保 VConsole 初始化后也能看到
  setTimeout(() => {
    console.log('[MobileBackground] 延迟打印资源', {
      file: mobileImageSrc.value,
      isAndroid: mobileImageMeta.value?.isAndroid,
      isIOS: mobileImageMeta.value?.isIOS,
      reason: mobileImageMeta.value?.reason,
      ua: mobileImageMeta.value?.ua
    });
  }, 500);
  
  // 如果是移动端，开始预加载 GIF
  if (!isPcView.value) {
    preloadGif();
  }

  // 确保在 DOM 渲染后更新视图类型
  // 使用 nextTick 确保在 DOM 渲染后再判断
  nextTick(() => {
    updateViewType();
    // 再次确认一次，防止初始化时判断错误
    setTimeout(() => {
  updateViewType();
      console.log('View type updated:', isPcView.value ? 'PC' : 'Mobile', 'Width:', window.innerWidth);
    }, 100);
  });
  
  window.addEventListener('resize', updateViewType);

  // 使用 nextTick 确保视频元素已经渲染
  nextTick(() => {
  const handleVideoEnded = (videoEl) => {
      // 重置视频时间到开始位置
    videoEl.currentTime = 0;

      // 移动端视频重播处理
      const attemptReplay = () => {
        // 确保视频保持静音状态
        videoEl.muted = true;
        videoEl.volume = 0;

        // 检查浏览器是否允许自动播放
        const playPromise = videoEl.play();

        if (playPromise !== undefined) {
          playPromise
              .then(() => {
                console.log("Video replay successful");
              })
              .catch(e => {
                console.warn("Auto-replay failed:", e.name, e.message);

                // 根据错误类型处理
                if (e.name === 'NotAllowedError') {
                  console.log("Browser blocked autoplay, video will resume when user interacts");
                  // 为移动端添加用户交互恢复播放的监听器
                  addReplayInteractionListener(videoEl);
                } else if (e.name === 'AbortError') {
                  console.log("Video play was interrupted, retrying...");
                  // 短暂延迟后重试
                  setTimeout(() => {
                    attemptReplay();
                  }, 500);
                } else {
                  console.log("Unknown replay error, will retry on next user interaction");
                  addReplayInteractionListener(videoEl);
                }
              });
        }
      };

        // 移动端特殊处理（仅 mp4 时）
        if (!isPcView.value && isMobileVideoMp4.value) {
        // 在移动端，延迟一小段时间再尝试重播，给浏览器时间处理
        setTimeout(() => {
          attemptReplay();
        }, 100);
      } else {
        // PC端直接尝试重播
        attemptReplay();
      }
    };

    // 添加用户交互恢复播放的监听器
    const addReplayInteractionListener = (videoEl) => {
      const replayOnInteraction = () => {
        if (videoEl.paused && videoEl.currentTime === 0) {
          console.log("Attempting to resume video on user interaction");
          videoEl.play().catch(e => {
            console.log("Resume play failed:", e.message);
          });
        }
        // 移除监听器，避免重复添加
        document.removeEventListener('touchstart', replayOnInteraction);
        document.removeEventListener('click', replayOnInteraction);
      };

      // 添加一次性的用户交互监听器
      document.addEventListener('touchstart', replayOnInteraction, { once: true, passive: true });
      document.addEventListener('click', replayOnInteraction, { once: true, passive: true });
    };

    // 根据当前视图类型播放对应的视频
    if (isPcView.value) {
  if (pcVideoRef.value) {
        attachVideoDebugEvents(pcVideoRef.value, 'pc');
    pcVideoRef.value.addEventListener('ended', () =>
        handleVideoEnded(pcVideoRef.value)
    );
    pcVideoRef.value.play().catch(e => console.log("Initial PC play error:", e));
  }
    } else {
      if (isMobileVideoMp4.value && mobileVideoRef.value) {
        attachVideoDebugEvents(mobileVideoRef.value, 'mobile');
        // 设置非标准属性以支持不同浏览器
        mobileVideoRef.value.setAttribute('webkit-playsinline', 'true');
        mobileVideoRef.value.setAttribute('playsinline', 'true');
        mobileVideoRef.value.setAttribute('x5-playsinline', 'true');
        mobileVideoRef.value.setAttribute('x5-video-player-type', 'h5');
        mobileVideoRef.value.setAttribute('x5-video-player-fullscreen', 'true');
        mobileVideoRef.value.setAttribute('x5-video-orientation', 'portrait');
        mobileVideoRef.value.setAttribute('disablePictureInPicture', 'true');

    mobileVideoRef.value.addEventListener('ended', () =>
        handleVideoEnded(mobileVideoRef.value)
    );

        // 尝试自动播放视频（带重试机制）
        let retryCount = 0;
        const maxRetries = 10;

        const attemptAutoPlay = () => {
          if (!mobileVideoRef.value || !isMobileVideoMp4.value) {
            return false;
          }

          // 设置音量（确保静音）
          mobileVideoRef.value.volume = 0;
          mobileVideoRef.value.muted = true;

          // 检查视频是否已加载足够的资源
          if (mobileVideoRef.value.readyState < 2) {
            // readyState < 2 表示视频数据还未加载足够
            if (retryCount < maxRetries) {
              retryCount++;
              setTimeout(() => {
                attemptAutoPlay();
              }, 300);
              return false;
            }
          }

          // 尝试播放
          const playPromise = mobileVideoRef.value.play();

          if (playPromise !== undefined) {
            playPromise
                .then(() => {
                  console.log("Video auto-played successfully");
                  retryCount = 0; // 重置重试计数
                })
                .catch(e => {
                  console.log("Auto-play failed:", e.name, e.message);

                  // 根据错误类型采取不同策略
                  if (e.name === 'NotAllowedError') {
                    console.warn("视频自动播放被浏览器阻止，等待用户交互");
                    // 添加用户交互恢复播放监听器
                    addAutoplayInteractionListener();
                    return;
                  } else if (e.name === 'NotSupportedError') {
                    console.error("视频格式不被支持");
                    return;
                  } else if (e.name === 'AbortError') {
                    console.log("视频播放被中断，准备重试");
                  }

                  // 其他错误继续重试
                  if (retryCount < maxRetries) {
                    retryCount++;
                    console.log(`第 ${retryCount} 次重试播放视频...`);
                    setTimeout(() => {
                      attemptAutoPlay();
                    }, Math.min(500 * retryCount, 2000)); // 递增延迟，最大2秒
                  } else {
                    console.warn("达到最大重试次数，视频可能需要用户交互才能播放");
                    addAutoplayInteractionListener();
                  }
                });
          }

          return true;
        };

        // 添加自动播放失败时的用户交互监听器
        const addAutoplayInteractionListener = () => {
          const playOnInteraction = () => {
            if (mobileVideoRef.value && mobileVideoRef.value.paused) {
              console.log("用户交互触发，尝试播放视频");
              mobileVideoRef.value.muted = true;
              mobileVideoRef.value.volume = 0;

              const playPromise = mobileVideoRef.value.play();
              if (playPromise !== undefined) {
                playPromise
                    .then(() => {
                      console.log("用户交互播放成功");
                    })
                    .catch(err => {
                      console.log("用户交互播放失败:", err.message);
                    });
  }
            }
          };

          // 添加多种用户交互事件监听
          document.addEventListener('touchstart', playOnInteraction, { once: true, passive: true });
          document.addEventListener('touchend', playOnInteraction, { once: true, passive: true });
          document.addEventListener('click', playOnInteraction, { once: true, passive: true });

          // 为视频元素本身也添加监听
          if (mobileVideoRef.value) {
            mobileVideoRef.value.addEventListener('touchstart', playOnInteraction, { once: true, passive: true });
            mobileVideoRef.value.addEventListener('click', playOnInteraction, { once: true, passive: true });
          }
        };

        // 微信浏览器特殊处理
        if (isWeChatBrowser.value) {
          console.log("WeChat browser detected, initializing video playback");

          // 确保视频元素可见
          if (mobileVideoRef.value) {
            mobileVideoRef.value.style.display = 'block';
            mobileVideoRef.value.style.visibility = 'visible';
            console.log("Video element visibility ensured");
          }

          // 检测 WeixinJSBridge 是否已加载
          const checkWeixinJSBridge = () => {
            if (typeof window.WeixinJSBridge !== 'undefined') {
              // 检查 WeixinJSBridge 是否就绪
              if (window.WeixinJSBridge.ready) {
                console.log("WeixinJSBridge is ready");
                return true;
              } else if (typeof window.WeixinJSBridge.invoke === 'function') {
                // 如果 WeixinJSBridge 存在但 ready 属性不可用，检查是否有 invoke 方法
                console.log("WeixinJSBridge is available");
                return true;
              }
            }
            return false;
          };

          // 微信中播放视频的函数
          const playVideoInWeChat = (requireUserInteraction = false) => {
            if (!mobileVideoRef.value) {
              console.warn("Video element not found");
              return;
            }

            // 确保视频已加载
            if (mobileVideoRef.value.readyState === 0) {
              console.log("Video not loaded yet, loading...");
              // 触发加载
              mobileVideoRef.value.load();

              // 等待视频加载完成
              const loadHandler = () => {
                console.log("Video loaded, readyState:", mobileVideoRef.value.readyState);
                if (mobileVideoRef.value.readyState >= 2) {
                  if (requireUserInteraction) {
                    console.log("Video ready but requires user interaction");
                  } else {
                    attemptAutoPlay();
                  }
                }
                mobileVideoRef.value.removeEventListener('canplay', loadHandler);
              };
              mobileVideoRef.value.addEventListener('canplay', loadHandler);
              return;
            }

            // 如果视频已加载，尝试播放
            if (requireUserInteraction) {
              console.log("Video ready, waiting for user interaction");
            } else {
              attemptAutoPlay();
            }
          };

          // 立即检查一次
          if (checkWeixinJSBridge()) {
            // 如果已经加载，延迟一下确保视频元素完全渲染
            setTimeout(() => {
              playVideoInWeChat();
            }, 300);
          } else {
            // 如果未加载，监听 WeixinJSBridgeReady 事件
            const weixinReadyHandler = () => {
              console.log("WeixinJSBridge ready event fired");
              setTimeout(() => {
                playVideoInWeChat();
              }, 300);
              document.removeEventListener('WeixinJSBridgeReady', weixinReadyHandler);
            };

            document.addEventListener('WeixinJSBridgeReady', weixinReadyHandler, false);

            // 轮询检测 WeixinJSBridge（防止事件丢失）
            let pollCount = 0;
            const maxPollCount = 30; // 增加轮询次数到 30 次（约 9 秒）
            const pollInterval = setInterval(() => {
              pollCount++;
              if (checkWeixinJSBridge()) {
                clearInterval(pollInterval);
                console.log("WeixinJSBridge detected via polling");
                setTimeout(() => {
                  playVideoInWeChat();
                }, 300);
              } else if (pollCount >= maxPollCount) {
                clearInterval(pollInterval);
                // 即使 WeixinJSBridge 未加载，也尝试播放（可能需要用户交互）
                console.log("WeixinJSBridge not loaded after polling, attempting to play anyway");
                console.warn("Video may require user interaction to play in WeChat");
                setTimeout(() => {
                  playVideoInWeChat();
                }, 500);
              }
            }, 300);
          }

          // 监听视频加载事件（多重保障）
          const videoLoadHandler = (event) => {
            console.log("Video load event:", event.type);
            if (mobileVideoRef.value && mobileVideoRef.value.paused) {
              setTimeout(() => {
                attemptAutoPlay();
              }, 200);
            }
          };

          mobileVideoRef.value.addEventListener('loadedmetadata', videoLoadHandler);
          mobileVideoRef.value.addEventListener('loadeddata', videoLoadHandler);
          mobileVideoRef.value.addEventListener('canplay', videoLoadHandler);
          mobileVideoRef.value.addEventListener('canplaythrough', videoLoadHandler);

          // 添加用户交互监听（微信中可能需要用户点击才能播放）
          // 必须在用户交互事件中直接调用 play()，不能延迟
          const userInteractionHandler = () => {
            if (!mobileVideoRef.value) {
              return;
            }

            console.log("User interaction detected, attempting to play video immediately");

            // 确保视频已加载
            if (mobileVideoRef.value.readyState < 2) {
              console.log("Video not ready, loading first...");
              mobileVideoRef.value.load();
              // 等待视频加载完成
              const playWhenReady = () => {
                if (mobileVideoRef.value.readyState >= 2) {
                  console.log("Video ready, attempting to play");
                  mobileVideoRef.value.play().catch(e => {
                    console.error("Play failed in user interaction:", e);
                  });
                } else {
                  setTimeout(playWhenReady, 100);
                }
              };
              mobileVideoRef.value.addEventListener('canplay', playWhenReady, { once: true });
              return;
            }

            // 立即尝试播放（不延迟，必须在用户交互上下文中）
            mobileVideoRef.value.volume = 0;
            mobileVideoRef.value.muted = true;

            const playPromise = mobileVideoRef.value.play();
            if (playPromise !== undefined) {
              playPromise
                  .then(() => {
                    console.log("Video played successfully after user interaction");
                  })
                  .catch(e => {
                    console.error("Play failed in user interaction handler:", e);
                    console.error("Error name:", e.name, "message:", e.message);
                  });
            }
          };

          // 监听多种用户交互事件（不限制 once，确保能多次尝试）
          document.addEventListener('touchstart', userInteractionHandler, { passive: true });
          document.addEventListener('touchend', userInteractionHandler, { passive: true });
          document.addEventListener('click', userInteractionHandler, { passive: true });

          // 也监听视频元素本身的交互
          if (mobileVideoRef.value) {
            mobileVideoRef.value.addEventListener('touchstart', userInteractionHandler, { passive: true });
            mobileVideoRef.value.addEventListener('click', userInteractionHandler, { passive: true });
          }

        } else {
          // 非微信浏览器，立即尝试播放
          attemptAutoPlay();

          // 监听视频加载完成事件，再次尝试播放
          mobileVideoRef.value.addEventListener('loadeddata', () => {
            if (mobileVideoRef.value && mobileVideoRef.value.paused) {
              attemptAutoPlay();
            }
          });

          // 监听视频可以播放事件
          mobileVideoRef.value.addEventListener('canplay', () => {
            if (mobileVideoRef.value && mobileVideoRef.value.paused) {
              attemptAutoPlay();
            }
          });
        }
      }
    }

    // 确保播放正确的视频
    playVideo();
  });

  createStars();
  
  // 如果是移动端，延迟调整容器高度（确保 DOM 完全渲染）
  if (!isPcView.value) {
    setTimeout(() => {
      adjustContainerHeight();
    }, 500);
  }
});

onUnmounted(() => {
  window.removeEventListener('resize', updateViewType);
});

const createStars = () => {
  const starsContainer = document.getElementById('stars');
  if (!starsContainer) {
    console.warn('Stars container not found');
    return;
  }
  
  // 清空现有星星（防止重复创建）
  starsContainer.innerHTML = '';
  
  const starCount = 150;
  const frag = document.createDocumentFragment();

  for (let i = 0; i < starCount; i++) {
    const star = document.createElement('div');
    star.classList.add('star');

    // 随机大小 1~3 px
    const size = Math.random() * 2 + 1;
    star.style.width = `${size}px`;
    star.style.height = `${size}px`;

    // 随机位置
    star.style.left = `${Math.random() * 100}%`;
    star.style.top = `${Math.random() * 100}%`;

    // 随机动画延迟 & 持续时间
    star.style.animationDelay = `${Math.random() * 5}s`;
    star.style.animationDuration = `${2 + Math.random() * 3}s`;

    frag.appendChild(star);
  }

  starsContainer.appendChild(frag);
  console.log('Stars created successfully');
};
</script>

<style>
.fill-area{
  width: 62%;
  max-width: 1605px;
  margin: 0 auto;
  padding-top: 74px;
  padding-bottom: 106px;
}

.bg-primary {
  background-color: #1a237e;
}

.input-row {
  --label-width: 90px; /* PC 标签占位宽度，缩短让输入更贴近 */
  width: 40%;
  max-width: 642px;
  margin-top: 54px;
  display: flex;
  align-items: center;
  position: relative;
}

.input-frame {
  flex: 1;
  height: 82px;
  background: #212A87;
  border-radius: 16px;
  border: 2px solid #48519D;
}

.input-overlay {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 82px;
  background: transparent;
  border: none;
  outline: none;
  font-size: 1.5vw;
  font-weight: bold;
  padding-left: calc(var(--label-width) + 10px); /* 输入紧贴标签右侧 */
  color: #fff;
}

.input-label-inside {
  position: absolute;
  left: 12px; /* 标签再靠左一些 */
  top: 50%;
  transform: translateY(-50%);
  font-size: 1.5vw;
  font-weight: bold;
  pointer-events: none;
  white-space: nowrap;
  width: var(--label-width);
}

.contact-us{
  font-weight:bold;
  font-size: 2.5vw;
  padding-top: 0;
  padding-bottom: 10px;
}

.footer-copyright{
  font-weight: 400;
  font-size: 1.3vw;
  padding-top: 50px;
  padding-bottom: 0;
  text-align: center;
  white-space: normal;
  word-wrap: break-word;
}

.button-style{
  width: 40%;
  max-width: 642px;
  height: 80px;
  font-weight: bold;
  background: #4D98FF;
  border-radius: 16px;
  font-size: 1.5vw;
  margin-top: 50px;
}

.video-container {
  position: relative;
  width: 100%;
  height: 100vh;
  overflow: hidden;
  z-index: 0;
  background-color: #1a237e; /* 背景色，星空在其上方 */
}

.video-mask {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 3;
  background-color: transparent;
}

.stars-background {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
  overflow: hidden;
  pointer-events: none;
}

.star {
  position: absolute;
  background: #fff;
  border-radius: 50%;
  animation: twinkle linear infinite alternate;
}

.content-section {
  position: relative;
  width: 100%;
  min-height: 100vh;
  z-index: 10;
  background-color: #1a237e;
  padding-top: 0;
}

.fill-area{
  position: relative;
  z-index: 2;
}

.video-element {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  min-width: 100%;
  min-height: 100%;
  display: block;
  background-color: #1a237e;
  z-index: 2;
}

.mobile-video-element {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  min-width: 100%;
  min-height: 100%;
  display: block;
  background-color: #1a237e;
  z-index: 2;
}

.mobile-image-element {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: fill; /* 宽度贴合屏幕，高度单独拉伸 */
  min-width: 100%;
  min-height: 100%;
  display: block;
  background-color: #1a237e;
  z-index: 2;
}

/* picture 标签作为容器 */
picture.mobile-image-element {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: block;
}

/* img 标签填充整个容器 */
picture.mobile-image-element img,
img.mobile-image-element {
  width: 100%;
  height: 100%;
  object-fit: fill; /* 不保持比例，填满容器 */
  min-width: 100%;
  min-height: 100%;
  display: block;
}

@keyframes twinkle {
  0% {
    opacity: 0.3;
    transform: scale(0.8);
  }
  100% {
    opacity: 1;
    transform: scale(1.2);
  }
}

/* 媒体查询：页面宽度小于1125px */
@media (max-width: 1125px) {
  .video-container {
    position: relative;
    width: 100vw;
    height: auto; /* 移动端高度自适应图片高度 */
    min-height: 0; /* 移除最小高度限制 */
    z-index: 1;
    overflow: hidden;
  }

  .video-element {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: block;
    object-fit: cover;
  }

  .mobile-image-element {
    position: absolute;
    top: 0;
    left: 0;
    width: 100vw !important;
    max-width: 100vw !important;
    height: auto !important; /* 高度根据原始宽高比自动计算 */
    min-width: 0 !important; /* 移除最小宽度限制 */
    min-height: 0 !important; /* 移除最小高度限制 */
    display: block;
    object-fit: contain; /* 保持原始宽高比，完整显示图片 */
    background-color: #1a237e;
    box-sizing: border-box;
  }

  /* 移动端 picture 容器 */
  picture.mobile-image-element {
    position: absolute;
    top: 0;
    left: 0;
    width: 100vw !important;
    max-width: 100vw !important;
    height: auto !important; /* 高度自适应图片高度 */
    min-width: 0 !important; /* 移除最小宽度限制 */
    min-height: 0 !important; /* 移除最小高度限制 */
    display: block;
    box-sizing: border-box;
    overflow: visible; /* 允许内容显示完整 */
  }

  /* 移动端 img 标签 */
  picture.mobile-image-element img,
  img.mobile-image-element {
    max-width: 100vw !important;
    min-width: 0 !important; /* 移除最小宽度限制 */
    min-height: 0 !important; /* 移除最小高度限制 */
    display: block;
    box-sizing: border-box;
    /* 通过 JavaScript 直接设置宽高，不使用 object-fit 避免缩放模糊 */
    /* 图片渲染优化 - 使用高质量渲染 */
    image-rendering: -webkit-optimize-contrast; /* WebKit 高质量渲染 */
    image-rendering: -moz-crisp-edges; /* Firefox 优化 */
    image-rendering: auto; /* 让浏览器选择最佳渲染方式 */
    /* 硬件加速优化 */
    backface-visibility: hidden;
    -webkit-backface-visibility: hidden;
    transform: translateZ(0);
    -webkit-transform: translateZ(0);
  }

  .content-section {
    position: relative;
    width: 100%;
    min-height: auto;
    z-index: 10;
    background-color: #1a237e;
  }
  .fill-area{
    width: 67%;
    max-width: 752px;
    margin: 0 auto;
    padding-top: 27px;
    padding-bottom: 57px;
  }

  .contact-us{
    font-size: 5vw;
    padding-top: 0;
    margin-top: 0;
    margin-bottom: 10px;
  }

  .input-row {
    --label-width: 54px; /* 移动端标签占位宽度，减少宽度让输入区域可以向左偏移 */
    width: 86%;
    max-width: 644px;
    margin-top: 24px;
    display: flex;
    align-items: center;
    position: relative;
  }

  .input-frame {
    flex: 1;
    height: 50px;
    background: #212A87;
    border-radius: 12px;
    border: 2px solid #48519D;
  }

  .input-overlay {
    height: 50px;
    font-size: 3vw;
    padding-left: calc(var(--label-width) - 8px); /* 输入区域向左偏移，减少标签宽度后可以真正向左移动 */
  }

  .input-label-inside {
    left: 10px;
    font-size: 3vw;
    width: var(--label-width);
  }

  .button-style{
    width: 86%;
    max-width: 644px;
    height: 50px;
    background: #4D98FF;
    border-radius: 12px;
    font-size: 3vw;
    margin-top: 37px;
  }

  .footer-copyright{
    font-weight: 400;
    font-size: 2.6vw;
    padding-top: 50px;
    padding-bottom: 57px;
    white-space: normal;
    word-wrap: break-word;
  }

  /* 移动端 ElMessage 提示位置调整 - 显示在当前视口（可见区域）顶部偏下位置 */
  .el-message {
    top: 180px !important;
    transform: translateX(-50%) !important;
    margin-top: 0 !important;
    min-width: 200px;
    max-width: 80vw;
    left: 50% !important;
    margin-left: 0 !important;
    position: fixed !important;
    z-index: 9999 !important;
  }
}
</style>